import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Client portal API instance (uses client_token)
const clientPortalApi = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add client token to client portal requests
clientPortalApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('client_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Don't redirect if on client portal or other public pages
      const path = window.location.pathname;
      if (!path.startsWith('/client-portal') && !path.startsWith('/collect/') && path !== '/') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Handle client portal auth errors
clientPortalApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('client_token');
      localStorage.removeItem('client_data');
      // Don't redirect - let the component handle it
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  initAdmin: () => api.post('/setup/init-admin'),
  // 2FA
  verify2FA: (data) => api.post('/auth/2fa/verify', data),
  resend2FA: (data) => api.post('/auth/2fa/resend', data),
  get2FAStatus: () => api.get('/auth/2fa/status'),
  enable2FA: () => api.post('/auth/2fa/enable'),
  confirmEnable2FA: (data) => api.post('/auth/2fa/confirm-enable', data),
  disable2FA: () => api.post('/auth/2fa/disable'),
  // Trusted Devices
  getTrustedDevices: () => api.get('/auth/trusted-devices'),
  revokeTrustedDevice: (deviceId) => api.delete(`/auth/trusted-devices/${deviceId}`),
  revokeAllTrustedDevices: () => api.delete('/auth/trusted-devices'),
};

// Organizations (Super Admin only)
export const organizationsAPI = {
  getAll: () => api.get('/organizations'),
  getOne: (id) => api.get(`/organizations/${id}`),
  create: (data) => api.post('/organizations', data),
  update: (id, data) => api.put(`/organizations/${id}`, data),
  delete: (id) => api.delete(`/organizations/${id}`),
  // Features
  getFeatures: (orgId) => api.get(`/organizations/${orgId}/features`),
  updateFeatures: (orgId, features) => api.put(`/organizations/${orgId}/features`, features),
  getAllFeaturesSummary: () => api.get('/organizations/all/features-summary'),
  // Stripe Connect
  initiateStripeConnect: (orgId, originUrl) => api.post(`/organizations/${orgId}/stripe-connect?origin_url=${encodeURIComponent(originUrl)}`),
  getStripeConnectStatus: (orgId) => api.get(`/organizations/${orgId}/stripe-connect/status`),
  getStripeDashboard: (orgId) => api.post(`/organizations/${orgId}/stripe-connect/dashboard`),
  getStripeConnectConfig: () => api.get('/stripe-connect/config'),
  // Subscriptions
  getSubscriptionConfig: () => api.get('/subscriptions/config'),
  createSubscription: (data, originUrl) => api.post(`/subscriptions?origin_url=${encodeURIComponent(originUrl)}`, data),
  activateSubscription: (sessionId) => api.post(`/subscriptions/activate?session_id=${sessionId}`),
  getOrgSubscription: (orgId) => api.get(`/subscriptions/${orgId}`),
  updateSubscriptionSeats: (orgId, data) => api.put(`/subscriptions/${orgId}/seats`, data),
  cancelSubscription: (orgId) => api.post(`/subscriptions/${orgId}/cancel`),
  chargeSetupFee: (data, originUrl) => api.post(`/subscriptions/setup-fee?origin_url=${encodeURIComponent(originUrl)}`, data),
  getSetupFeeStatus: (sessionId) => api.get(`/subscriptions/setup-fee/status/${sessionId}`),
  getBillingOverview: () => api.get('/admin/billing/overview'),
};

// Admin User Management
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  getUser: (id) => api.get(`/admin/users/${id}`),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getEncryptionStatus: () => api.get('/admin/encryption/status'),
  runEncryptionMigration: () => api.post('/admin/encryption/migrate'),
};

// Cases (formerly Clients)
export const casesAPI = {
  getAll: (params) => api.get('/cases', { params }),
  getOne: (id) => api.get(`/cases/${id}`),
  create: (data, organizationId) => api.post('/cases', data, { params: { organization_id: organizationId } }),
  update: (id, data) => api.put(`/cases/${id}`, data),
  delete: (id) => api.delete(`/cases/${id}`),
  exportCSV: () => api.get('/cases/export/csv', { responseType: 'blob' }),
  scanForm: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const startRes = await api.post('/cases/scan-form/start', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 55000
    });
    const jobId = startRes.data.job_id;
    const deadline = Date.now() + 300000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 3000));
      const st = await api.get(`/cases/scan-form/jobs/${jobId}`, { timeout: 30000 });
      if (st.data.status === 'completed') return { data: st.data.result };
      if (st.data.status === 'failed') {
        return { data: { success: false, error: st.data.error || 'Form scanning failed. Please try again.' } };
      }
    }
    return { data: { success: false, error: 'Scan timed out. Please try again.' } };
  },
  extractFormIntoCase: (caseId, file, formType) => {
    const formData = new FormData();
    formData.append('file', file);
    if (formType) formData.append('form_type', formType);
    return api.post(`/cases/${caseId}/extract-form`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000
    });
  },
  applyExtractedData: (caseId, data) => api.put(`/cases/${caseId}/apply-extracted`, data),
  batchScanForms: (files) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return api.post('/cases/batch-scan', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000
    });
  },
  downloadTemplate: () => api.get('/cases/import/template', { responseType: 'blob' }),
  bulkImport: (file, organizationId, skipDuplicates = true) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/cases/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { organization_id: organizationId, skip_duplicates: skipDuplicates }
    });
  },
  startBulkImport: (file, organizationId, skipDuplicates = true) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/cases/import/start', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { organization_id: organizationId, skip_duplicates: skipDuplicates }
    });
  },
  getImportStatus: (jobId) => api.get(`/cases/import/status/${jobId}`),
};

// Status Updates
export const statusAPI = {
  getUpdates: (caseId) => api.get(`/cases/${caseId}/status-updates`),
  createUpdate: (caseId, data) => api.post(`/cases/${caseId}/status-updates`, data),
};

// Documents
export const documentsAPI = {
  getAll: (caseId) => api.get(`/cases/${caseId}/documents`),
  upload: (caseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/cases/${caseId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (docId) => api.get(`/documents/${docId}/download`, { responseType: 'blob' }),
  delete: (docId) => api.delete(`/documents/${docId}`),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Reports
export const reportsAPI = {
  getStats: () => api.get('/reports/stats'),
  exportReport: (reportType = 'full', options = {}) => {
    const params = { 
      report_type: reportType,
      ...options 
    };
    return api.get('/reports/export', { 
      params,
      responseType: 'blob' 
    });
  },
  exportPDF: (options = {}) => {
    return api.get('/reports/export/pdf', { 
      params: options,
      responseType: 'blob' 
    });
  },
  getSettings: () => api.get('/reports/settings'),
  updateSettings: (settings) => api.put('/reports/settings', settings),
};

// Forms (Auto-Fill)
export const formsAPI = {
  getAvailable: () => api.get('/forms/available'),
  fillForm: (formId, caseId) => api.get(`/forms/fill/${formId}/${caseId}`, {
    responseType: 'blob'
  }),
  generateFilled: (formId, caseId) => api.get(`/forms/fill/${formId}/${caseId}`, {
    responseType: 'blob'
  }),
  getCompletionScores: (caseId) => api.get(`/forms/completion-scores/${caseId}`),
  getChecklist: (formId, caseId) => api.get(`/forms/${formId}/checklist/${caseId}`),
  requestData: (formId, caseId, sendEmail) => api.post(`/forms/request-data/${formId}/${caseId}?send_email_flag=${sendEmail}`),
  getCaseDataRequests: (caseId) => api.get(`/cases/${caseId}/data-requests`),
};

// Client Portals (for org users to manage)
export const clientPortalsAPI = {
  create: (data) => api.post('/client-portals', data),
  list: () => api.get('/client-portals'),
  getForCase: (caseId) => api.get(`/client-portals/case/${caseId}`),
  revoke: (portalId) => api.delete(`/client-portals/${portalId}`),
  regenerateCode: (portalId) => api.post(`/client-portals/${portalId}/regenerate`),
};

// Client Portal (for end clients)
export const clientAPI = {
  login: (email, accessCode, caseId) => api.post('/client-portal/login', { email, access_code: accessCode, case_id: caseId }),
  getCase: () => clientPortalApi.get('/client-portal/case'),
  getDocuments: () => clientPortalApi.get('/client-portal/documents'),
  uploadDocument: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return clientPortalApi.post('/client-portal/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getForms: () => clientPortalApi.get('/client-portal/forms'),
  fillForm: (formId) => clientPortalApi.get(`/client-portal/forms/fill/${formId}`, {
    responseType: 'blob'
  }),
  updateProfile: (data) => clientPortalApi.put('/client-portal/profile', data),
  getProfileCompletion: () => clientPortalApi.get('/client-portal/profile/completion'),
  // Client Invoices
  getInvoices: () => clientPortalApi.get('/client/invoices'),
  getInvoice: (invoiceId) => clientPortalApi.get(`/client/invoices/${invoiceId}`),
  createCheckout: (invoiceId, originUrl) => clientPortalApi.post(`/invoices/${invoiceId}/checkout?origin_url=${encodeURIComponent(originUrl)}`),
  getPaymentStatus: (sessionId) => clientPortalApi.get(`/invoices/payment/status/${sessionId}`),
  
  // Client Meetings
  getMeetings: () => clientPortalApi.get('/client-portal/meetings'),
  getAvailableSlots: () => clientPortalApi.get('/client-portal/available-slots'),
  bookSlot: (slotId, selectedSlotIndex) => clientPortalApi.post('/client-portal/book-slot', { slot_id: slotId, selected_slot_index: selectedSlotIndex }),
  requestReschedule: (meetingId, reason) => clientPortalApi.post(`/client-portal/meetings/${meetingId}/reschedule-request`, { meeting_id: meetingId, reason }),
};

// Notification Center (Client Profile Updates)
export const notificationCenterAPI = {
  getClientUpdates: (params = {}) => api.get('/notifications/client-updates', { params }),
  markAsRead: (notificationId) => api.post(`/notifications/client-updates/${notificationId}/read`),
  markAllAsRead: () => api.post('/notifications/client-updates/read-all'),
  getUnreadCount: () => api.get('/notifications/client-updates/unread-count'),
};

// Email Templates (Super Admin)
export const emailTemplatesAPI = {
  getAll: () => api.get('/email-templates'),
  update: (templateType, data) => api.put(`/email-templates/${templateType}`, data),
  reset: (templateType) => api.post(`/email-templates/${templateType}/reset`),
  preview: (templateType, data) => api.post(`/email-templates/${templateType}/preview`, data),
};

// Invoice Management (Staff/Admin)
export const invoicesAPI = {
  getAll: (params = {}) => api.get('/invoices', { params }),
  get: (invoiceId) => api.get(`/invoices/${invoiceId}`),
  create: (data) => api.post('/invoices', data),
  update: (invoiceId, data) => api.put(`/invoices/${invoiceId}`, data),
  delete: (invoiceId) => api.delete(`/invoices/${invoiceId}`),
  sendEmail: (invoiceId) => api.post(`/invoices/${invoiceId}/send`),
  createCheckout: (invoiceId, originUrl) => api.post(`/invoices/${invoiceId}/checkout?origin_url=${encodeURIComponent(originUrl)}`),
};

// Meeting/Calendar Management (Staff/Admin)
export const meetingsAPI = {
  getAll: (params = {}) => api.get('/meetings', { params }),
  get: (meetingId) => api.get(`/meetings/${meetingId}`),
  create: (data) => api.post('/meetings', data),
  update: (meetingId, data) => api.put(`/meetings/${meetingId}`, data),
  delete: (meetingId) => api.delete(`/meetings/${meetingId}`),
  generateLink: (meetingId) => api.post(`/meetings/${meetingId}/generate-link`),
  setVideoLink: (meetingId, videoLink) => api.put(`/meetings/${meetingId}/video-link`, { video_link: videoLink }),
  
  // Available Slots
  getAvailableSlots: (params = {}) => api.get('/available-slots', { params }),
  createAvailableSlots: (data) => api.post('/available-slots', data),
  
  // Google Calendar
  getGoogleCalendarStatus: () => api.get('/google-calendar/status'),
  connectGoogleCalendar: (accessToken, refreshToken) => api.post('/google-calendar/connect', { access_token: accessToken, refresh_token: refreshToken }),
  exchangeGoogleSession: (sessionId) => api.post('/google-calendar/exchange-session', { session_id: sessionId }),
  disconnectGoogleCalendar: () => api.delete('/google-calendar/disconnect'),
  
  // Video Platforms
  getVideoPlatformStatus: () => api.get('/video-platforms/status'),
};

// Custom Field Definitions
export const customFieldsAPI = {
  list: () => api.get('/custom-fields'),
  create: (data) => api.post('/custom-fields', data),
  delete: (fieldId) => api.delete(`/custom-fields/${fieldId}`),
};

// AI Chat Assistant
export const chatAPI = {
  listSessions: () => api.get('/chat/sessions'),
  getSession: (sessionId) => api.get(`/chat/sessions/${sessionId}`),
  createSession: (data) => api.post('/chat/sessions', data),
  deleteSession: (sessionId) => api.delete(`/chat/sessions/${sessionId}`),
  sendMessage: (sessionId, data) => api.post(`/chat/sessions/${sessionId}/messages`, data),
  getRecommendations: (sessionId) => api.post(`/chat/sessions/${sessionId}/recommendations`),
};

// Weekly Digest
export const digestAPI = {
  getSettings: () => api.get('/digest/settings'),
  updateSettings: (data) => api.put('/digest/settings', data),
  getHistory: () => api.get('/digest/history'),
  sendNow: () => api.post('/digest/send-now'),
};

// eFiling
export const filingAPI = {
  getForms: () => api.get('/filings/forms'),
  list: (status) => api.get('/filings' + (status ? `?status=${status}` : '')),
  get: (id) => api.get(`/filings/${id}`),
  create: (data) => api.post('/filings', data),
  update: (id, data) => api.put(`/filings/${id}`, data),
  delete: (id) => api.delete(`/filings/${id}`),
  getForCase: (caseId) => api.get(`/filings/case/${caseId}`),
  stats: () => api.get('/filings/stats/summary'),
};

export default api;
